import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from './ui/Button';
import { apiService } from '../services/api';
import type { AnalysisResults } from '@printability/shared-types';

interface FileUploadProps {
  onAnalysisComplete: (analysis: {
    analysisId: string;
    filename: string;
    results: AnalysisResults;
    processingTime: number;
    fileData: ArrayBuffer;
  }) => void;
  onError: (error: string) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onAnalysisComplete,
  onError,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analysisType, setAnalysisType] = useState<'basic' | 'advanced'>('advanced');

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    const fullySupported = ['.stl', '.obj'];
    const comingSoon = ['.gltf', '.glb', '.ply'];
    const allExtensions = [...fullySupported, ...comingSoon];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!allExtensions.includes(fileExtension)) {
      onError(`Unsupported file format. Currently supported: ${fullySupported.join(', ')}. Coming soon: ${comingSoon.join(', ')}`);
      return;
    }

    // Warn about formats that are coming soon
    if (comingSoon.includes(fileExtension)) {
      onError(`${fileExtension.toUpperCase()} support is coming soon! Please use STL or OBJ format for now.`);
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      onError(`File too large. Maximum size: ${maxSize / 1024 / 1024}MB`);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Store file data for 3D viewer
      const fileData = await file.arrayBuffer();

      const result = await apiService.uploadAndAnalyze(
        file,
        analysisType,
        (progress) => setUploadProgress(progress)
      );

      onAnalysisComplete({
        analysisId: result.analysisId,
        filename: result.filename,
        results: result.results,
        processingTime: result.processingTime,
        fileData,
      });
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [analysisType, onAnalysisComplete, onError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/octet-stream': ['.stl'],
      'text/plain': ['.obj'],
      'model/stl': ['.stl'],
      // Coming soon: GLTF, GLB, PLY
      // 'model/gltf+json': ['.gltf'],
      // 'model/gltf-binary': ['.glb'],
      // 'application/json': ['.gltf'],
    },
    multiple: false,
    disabled: isUploading,
  });

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Analysis Type Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Analysis Type
        </label>
        <div className="flex space-x-4">
          {(['basic', 'advanced'] as const).map((type) => (
            <label key={type} className="flex items-center">
              <input
                type="radio"
                name="analysisType"
                value={type}
                checked={analysisType === type}
                onChange={(e) => setAnalysisType(e.target.value as typeof type)}
                disabled={isUploading}
                className="mr-2"
              />
              <span className="capitalize">{type}</span>
            </label>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-1">
          {analysisType === 'basic' && 'Quick overhang detection and basic geometry metrics'}
          {analysisType === 'advanced' && 'Comprehensive analysis with wall thickness, support detection, and optimization suggestions'}
        </p>
      </div>

      {/* File Drop Zone */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
          }
          ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />

        {isUploading ? (
          <div className="space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <div>
              <p className="text-lg font-medium text-gray-900">Processing your model...</p>
              <p className="text-sm text-gray-500">This may take a few moments</p>
              {uploadProgress > 0 && (
                <div className="mt-2">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{uploadProgress}% complete</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg fill="none" stroke="currentColor" viewBox="0 0 48 48">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                />
              </svg>
            </div>

            {isDragActive ? (
              <div>
                <p className="text-lg font-medium text-blue-600">Drop your 3D model here</p>
                <p className="text-sm text-gray-500">Release to upload and analyze</p>
              </div>
            ) : (
              <div>
                <p className="text-lg font-medium text-gray-900">
                  Drag & drop your 3D model here
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  or click to browse files
                </p>
                <Button variant="outline" size="sm">
                  Choose File
                </Button>
              </div>
            )}

            <div className="text-xs text-gray-400">
              <p>Supported formats: STL, OBJ</p>
              <p>Maximum file size: 50MB</p>
            </div>
          </div>
        )}
      </div>

      {/* Analysis Type Info */}
      <div className="mt-4 text-sm text-gray-600">
        <h4 className="font-medium mb-2">What will be analyzed:</h4>
        <ul className="space-y-1">
          <li className="flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            Geometry metrics (volume, surface area, complexity)
          </li>
          <li className="flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            Overhang detection and support requirements
          </li>
          {analysisType === 'advanced' && (
            <>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                Wall thickness analysis
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                Optimization suggestions
              </li>
            </>
          )}
          {false && ( // Removed full analysis type
            <>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                Bridging and warping analysis
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                AI-powered insights and recommendations
              </li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
};
